import FormInput, { FormProps } from "../input/FormInput";
import Select, { components, InputProps, StylesConfig } from "react-select";
import { customStyles, Input } from "./autocomplete.style";
import DropdownIndicator from "./DropDownIndicator";
import { useState, useEffect } from "react";

// Test and review
export interface AutocompleteProps
  extends Omit<FormProps, "onBlur" | "onChange" | "value">,
    Partial<InputProps> {
  styles?: StylesConfig;
  classNamePrefix?: string;
  setFieldValue?: (fieldName: string, value: any) => void;
  handleBlur?: (e?: any) => void; // refine
  isSearch?: boolean;
  inputName?: string;
  inputValue?: string | string[] | number;
}

type Option = {
  label: string;
  value: any;
  [key: string]: any; // This allows the object to have any additional properties
};

const Autocomplete = ({
  name,
  errorMessage,
  label,
  options,
  styles,
  isDisabled,
  setFieldValue,
  isImportant,
  placeholder,
  value,
  className,
  classNamePrefix,
  isMulti,
  handleBlur,
  showToggleBox,
  toggleBoxName,
  toggleBoxLabel,
  toggleBoxValue,
  isSearch,
  inputName,
  inputValue,
  qapi,
  showBadges,
}: AutocompleteProps) => {
  const [checked, setChecked] = useState(toggleBoxValue);

  // Sync local checked state with external toggleBoxValue when it changes
  useEffect(() => {
    setChecked(toggleBoxValue);
  }, [toggleBoxValue]);

  // This function handles value change for the select field. Select options must fit the
  // {label: string, value: any} template to work.

  function handleSelectChange(value: any) {
    const selectedValue = isMulti
      ? value.map((value: { value: Option }) => value.value)
      : value.value;
    if (setFieldValue) {
      setFieldValue(name as string, selectedValue);
    }
  }

  function handleInputChange(e: any) {
    if (setFieldValue) {
      setFieldValue(inputName as string, e.target.value);
    }
  }

  function onCheck(e: any) {
    if (setFieldValue && name && inputName) {
      e.target.checked ? setFieldValue(name, "") : setFieldValue(inputName, "");
    }
    setChecked(e.target.checked);
  }

  // This function checks that options are valid react-select options.
  function isValidRSOptions(value: unknown): value is Option[] {
    return true;
  }

  // This function returns selected values for the select component

  function getSelectedOption() {
    if (isValidRSOptions(options) && value) {
      if (!isMulti) {
        return options?.find((option) => option?.value === value) || "";
      } else {
        return options?.filter((option) =>
          (value as any[]).includes(option.label)
        );
      }
    }
  }

  function getOptionLabel(option: any): string {
    return option.label;
  }

  function getOptionValue(option: any) {
    return option.value;
  }

  function onFieldBlur() {
    handleBlur && handleBlur({ target: { name: name } });
  }

  return (
    <>
      <FormInput
        data-testid={`autocomplete-${name}`}
        label={label}
        name={name}
        errorMessage={errorMessage}
        isImportant={isImportant}
        showToggleBox={showToggleBox}
        toggleBoxName={toggleBoxName}
        setFieldValue={setFieldValue}
        toggleBoxLabel={toggleBoxLabel}
        toggleBoxValue={toggleBoxValue}
        onCheck={onCheck}
        disabled={isDisabled}
        qapi={qapi}
        showBadges={showBadges}
      >
        {!checked ? (
          <Select
            data-testid={`select-${name}`}
            key={`${name}-${
              typeof value === "string" ? value : [value].flat()?.join()
            }`}
            name={name}
            options={options}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            className={className}
            classNamePrefix={classNamePrefix}
            styles={{ ...customStyles, ...styles }}
            isDisabled={isDisabled}
            onChange={handleSelectChange}
            placeholder={placeholder}
            value={getSelectedOption()}
            onBlur={onFieldBlur}
            isMulti={isMulti}
            components={{
              DropdownIndicator: isSearch
                ? DropdownIndicator
                : components.DropdownIndicator,
            }}
            aria-label={name}
          />
        ) : (
          <Input
            data-testid={`input-${inputName}`}
            name={inputName}
            placeholder={placeholder}
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            disabled={isDisabled}
            aria-label={name}
          />
        )}
      </FormInput>
    </>
  );
};

export default Autocomplete;
